import type { ExpressionSpecification } from 'mapbox-gl';
import {
  buildTrailheadCountTextField,
  buildTrailheadIconCollapsedOffset,
  buildTrailheadIconImage,
  buildTrailheadIconOffset,
  buildTrailheadNameTextOffset,
  buildTrailheadSelectedIconImage,
  buildTrailheadTextOffset
} from '../../utils/buildTrailheadIconExpressions';
import { getTrailheadPinStyle } from '../../utils/trailheadPinVariant';
import TrailheadPinLayers, { type TrailheadPinLayerProps } from '../TrailheadPinLayers/TrailheadPinLayers';

type TrailheadVectorPinLayerProps = TrailheadPinLayerProps & {
  trailCountExpression: ExpressionSpecification;
};

/**
 * The circle pins: a circle, with a count banner for trailheads with more than one trail. A hovered or selected pin
 * swaps to the selected pin.
 */
const TrailheadVectorPinLayer = ({
  isHoveredExpression,
  isSelectedExpression,
  pinVariant,
  trailCountExpression,
  ...layerProps
}: TrailheadVectorPinLayerProps) => {
  const pinStyle = getTrailheadPinStyle(pinVariant);
  const variantExpressions = {
    isClosedExpression: layerProps.isClosedExpression,
    isCompletedExpression: layerProps.isCompletedExpression,
    isSavedExpression: layerProps.isSavedExpression
  };
  const isActiveExpression: ExpressionSpecification = ['any', isHoveredExpression, isSelectedExpression];

  return (
    <TrailheadPinLayers
      {...layerProps}
      collapsedActiveLayout={{
        'icon-image': buildTrailheadSelectedIconImage(variantExpressions, pinStyle),
        'icon-offset': buildTrailheadIconCollapsedOffset(isActiveExpression)
      }}
      expandedLayout={{
        'icon-image': buildTrailheadIconImage(variantExpressions, isActiveExpression, trailCountExpression, pinStyle),
        'icon-offset': buildTrailheadIconOffset(isActiveExpression),
        // The count owns an expanded pin's `text-field` and its offset, so the names are on layers of their own
        'text-field': buildTrailheadCountTextField(isActiveExpression, trailCountExpression),
        'text-offset': buildTrailheadTextOffset(variantExpressions, isActiveExpression)
      }}
      isActiveExpression={isActiveExpression}
      nameTextOffset={buildTrailheadNameTextOffset(trailCountExpression)}
    />
  );
};

export default TrailheadVectorPinLayer;
